import { Apple, Facebook } from '@ori-ui/icons';
import { Grid, Typography, FormHelperText, Collapse, Box } from '@ori-ui/mui';
import type {
  GlobalCustomerRegistrationsRegistrationDto,
  GlobalCustomerRegistrationsRegistrationDtoLoginType,
} from '@ori/global-api-client-react';
import type { StaticCustomerRegistrationsMetadataRegistrationMetadataDto } from '@ori/static-api-client-react';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { ContentKeys, Localization } from '../../../Localization';
import { defaultTranslations } from '../../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { GetContent, useConfiguration, useFormError } from '../../../contexts';
import { useTranslations } from '../../../hooks/useTranslations_simplified-registration-BrandPartner';
import { TranslationKey } from '../../../models';
import { getErrorLocalizationByCode } from '../../../utils';
import { PasswordFieldByConfig, PhoneFieldByConfig, TextFormFieldByConfig } from '../../FieldsByConfig';
import { FormValidationSummary } from '../../FormValidationSummary';
import { GoogleIcon } from '../../GoogleIcon';
import {
  StyledForm,
  Separator,
  SocialButtton,
  SocialWrapper,
  SubmitButtton,
  IconWrapper,
  HiddenInput,
  IconText,
  StyledLink,
  LegalNotice,
  AddInvitationCode,
} from './PreStepFormStyledComponent';
import { PublicSponsorBox } from './PublicSponsorBox';

interface IPreStepFormProps {
  config?: StaticCustomerRegistrationsMetadataRegistrationMetadataDto;
  onSubmitForm: (data: GlobalCustomerRegistrationsRegistrationDto) => void;
  isSuccessSubmit: boolean;
}

const getCorrectTabIndex = (tabIndex?: number) => (typeof tabIndex === 'number' ? tabIndex + 1 : 0);

export const PreStepForm: FC<IPreStepFormProps> = (props) => {
  const { config, isSuccessSubmit, onSubmitForm } = props;

  const [isInvitationFieldVisible, setIsInvitationFieldVisible] = useState(false);
  const { getTestIdProps } = useTestId();
  const { validationErrorCode, referral, registrationType } = useConfiguration();
  const { formatMessage } = useTranslations();

  const methods = useForm<GlobalCustomerRegistrationsRegistrationDto>({
    reValidateMode: 'onChange',
    mode: 'onChange',
  });

  const { formState, watch, register, setValue } = methods;
  const { isValid, isSubmitted, isSubmitting } = formState;
  const formValues = watch();

  const {
    context: { errorData },
  } = useFormError();
  const { customErrors } = errorData;

  const invalidCodeErrors = customErrors?.filter(
    (er) =>
      !Object.keys(formValues)
        .map((val) => val.toLowerCase())
        .includes(er.fieldName?.toLowerCase() ?? ''),
  );

  const socialValidationMessage = getErrorLocalizationByCode(validationErrorCode);

  const isShowCustomValidationMessage = Boolean(!isSubmitted && validationErrorCode);

  const BPAndLink = !(registrationType === 'Consultant' && referral?.ReferralType === 'ReferralLink');

  const visibleByType = registrationType === 'VipCustomer' ? true : BPAndLink;

  const isShowSponsorBox = referral?.CustomerId && visibleByType;

  const loginWithSocialNetwork = (loginType: GlobalCustomerRegistrationsRegistrationDtoLoginType) => {
    setValue('loginType', loginType);
    setValue('contactEmail', undefined);
    setValue('contactTelephone', undefined);
    setValue('password', undefined);
    methods.clearErrors();
    methods.handleSubmit(onSubmitForm)();
  };

  useEffect(() => {
    if (formValues.contactEmail) {
      setValue('loginType', 'Email');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps --- not needed other property
  }, [formValues.contactEmail]);

  useEffect(() => {
    if (formValues.contactTelephone) {
      setValue('loginType', 'Telephone');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps --- not needed other property
  }, [formValues.contactTelephone]);

  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- we need logical operator || */
  const isAnySocialNetworkAvailable = Boolean(
    config?.appleLogin?.isAvailable || config?.facebookLogin?.isAvailable || config?.googleLogin?.isAvailable,
  );
  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing -- we need logical operator || */

  return (
    <FormProvider {...methods}>
      {isShowCustomValidationMessage ? (
        <FormHelperText error>
          <Localization {...socialValidationMessage} />
        </FormHelperText>
      ) : null}
      <FormValidationSummary invalidPropertyErrors={invalidCodeErrors} />
      <StyledForm
        noValidate
        autoComplete="off"
        onSubmit={methods.handleSubmit(onSubmitForm)}
      >
        <Grid
          container
          justifyContent="center"
        >
          <HiddenInput {...register('loginType')} />
          <TextFormFieldByConfig
            enableLowercaseLetters
            name="contactEmail"
            label={
              <Localization
                id={TranslationKey.Prestep_Email}
                defaultMessage={defaultTranslations.Prestep_Email}
              />
            }
            tooltipText={
              <Localization
                id={TranslationKey.Prestep_TooltipEmail}
                defaultMessage={defaultTranslations.Prestep_TooltipEmail}
              />
            }
            customPlaceholder={formatMessage(TranslationKey.Prestep_PlaceholderEmail)}
            keyboardType="email"
            {...config?.contactEmail}
          />
          <PhoneFieldByConfig
            name="contactTelephone"
            label={
              <Localization
                id={TranslationKey.Prestep_Phone}
                defaultMessage={defaultTranslations.Prestep_Phone}
              />
            }
            labelWithPrefix={
              <Localization
                id={TranslationKey.Prestep_PhoneWithPrefix}
                defaultMessage={defaultTranslations.Prestep_PhoneWithPrefix}
              />
            }
            tooltipText={
              <Localization
                id={TranslationKey.Prestep_TooltipPhone}
                defaultMessage={defaultTranslations.Prestep_TooltipPhone}
              />
            }
            tooltipWithPrefix={
              <Localization
                id={TranslationKey.Prestep_TooltipPhoneWithPrefix}
                defaultMessage={defaultTranslations.Prestep_TooltipPhoneWithPrefix}
              />
            }
            customPlaceholder={formatMessage(TranslationKey.Prestep_PlaceholderPhone)}
            customPlaceholderWithPrefix={formatMessage(TranslationKey.Prestep_PlaceholderPhoneWithPrefix)}
            {...config?.contactTelephone}
          />
          {isShowSponsorBox ? <PublicSponsorBox displayOrder={config?.invitationCode?.displayOrder} /> : null}

          {config?.invitationCode?.isAvailable && !referral?.CustomerId ? (
            <>
              {isInvitationFieldVisible ? null : (
                <Grid
                  item
                  xs={12}
                  sm={12}
                  style={{ order: config.invitationCode.displayOrder }}
                >
                  <Box
                    maxWidth={500}
                    margin="0 auto"
                  >
                    <AddInvitationCode onClick={() => setIsInvitationFieldVisible(!isInvitationFieldVisible)}>
                      <Localization
                        id={TranslationKey.Prestep_InvitationCodeTitle}
                        defaultMessage={defaultTranslations.Prestep_InvitationCodeTitle}
                      />
                    </AddInvitationCode>
                  </Box>
                </Grid>
              )}

              <Collapse
                in={isInvitationFieldVisible}
                sx={{ width: '100%', order: config.invitationCode.displayOrder }}
              >
                <TextFormFieldByConfig
                  isEditable
                  name="invitationCode"
                  label={
                    <Localization
                      id={TranslationKey.Prestep_InvitationCode}
                      defaultMessage={defaultTranslations.Prestep_InvitationCode}
                    />
                  }
                  tooltipText={
                    <Localization
                      id={TranslationKey.Prestep_InvitationCodeTooltip}
                      defaultMessage={defaultTranslations.Prestep_InvitationCodeTooltip}
                    />
                  }
                  keyboardType="text"
                  {...config.invitationCode}
                />
              </Collapse>
            </>
          ) : null}

          <PasswordFieldByConfig
            name="password"
            label={
              <Localization
                id={TranslationKey.Prestep_Password}
                defaultMessage={defaultTranslations.Prestep_Password}
              />
            }
            customPlaceholder={formatMessage(TranslationKey.Prestep_PlaceholderPassword)}
            {...config?.password}
          />
          {config?.legalNoticeText?.isEnabled ? (
            <Grid
              item
              xs={12}
              sm={12}
              style={{ order: config.legalNoticeText.displayOrder ?? 7 }}
            >
              <LegalNotice
                variant="caption"
                {...getTestIdProps('prestep-legal-notice')}
              >
                <Localization
                  id={TranslationKey.Prestep_LegalNotice}
                  defaultMessage={defaultTranslations.Prestep_LegalNotice}
                  values={{
                    TermsAndConditions_Link: (
                      <StyledLink
                        href={GetContent(ContentKeys.TermsAndConditions_Url)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Localization
                          id={TranslationKey.Prestep_LegalNotice_TermsAndConditions_Label}
                          defaultMessage={defaultTranslations.Prestep_LegalNotice_TermsAndConditions_Label}
                        />
                      </StyledLink>
                    ),
                    PrivacyPolicy_Link: (
                      <StyledLink
                        href={GetContent(ContentKeys.PrivacyPolicy_Url)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Localization
                          id={TranslationKey.Prestep_LegalNotice_PrivacyPolicy_Label}
                          defaultMessage={defaultTranslations.Prestep_LegalNotice_PrivacyPolicy_Label}
                        />
                      </StyledLink>
                    ),
                  }}
                />
              </LegalNotice>
            </Grid>
          ) : null}
          <SubmitButtton
            variant="contained"
            color="primary"
            type="submit"
            // eslint-disable-next-line jsx-a11y/tabindex-no-positive -- we need this specific number for current move by tab
            tabIndex={3}
            disabled={!isValid || isSubmitting || isSuccessSubmit}
            {...getTestIdProps('submit-btn-preStepForm')}
          >
            <Localization
              id={TranslationKey.Prestep_Button}
              defaultMessage={defaultTranslations.Prestep_Button}
            />
          </SubmitButtton>
        </Grid>
      </StyledForm>
      {isAnySocialNetworkAvailable ? (
        <>
          <Separator>
            <Typography variant="body2">
              <Localization
                id={TranslationKey.Prestep_Or}
                defaultMessage={defaultTranslations.Prestep_Or}
              />
            </Typography>
          </Separator>
          <SocialWrapper>
            {config?.googleLogin?.isAvailable ? (
              <SocialButtton
                color="inherit"
                sx={{ order: config.googleLogin.displayOrder }}
                disabled={isSubmitting}
                tabIndex={getCorrectTabIndex(config.googleLogin.displayOrder)}
                onClick={() => loginWithSocialNetwork('Google')}
              >
                <IconWrapper>
                  <GoogleIcon />
                </IconWrapper>
                <IconText variant="body1">
                  <Localization
                    id={TranslationKey.Prestep_Google}
                    defaultMessage={defaultTranslations.Prestep_Google}
                  />
                </IconText>
              </SocialButtton>
            ) : null}
            {config?.appleLogin?.isAvailable ? (
              <SocialButtton
                color="inherit"
                sx={{ order: config.appleLogin.displayOrder }}
                disabled={isSubmitting}
                tabIndex={getCorrectTabIndex(config.appleLogin.displayOrder)}
                onClick={() => loginWithSocialNetwork('Apple')}
              >
                <IconWrapper>
                  <Apple sx={{ fontSize: '29px' }} />
                </IconWrapper>
                <IconText variant="body1">
                  <Localization
                    id={TranslationKey.Prestep_Apple}
                    defaultMessage={defaultTranslations.Prestep_Apple}
                  />
                </IconText>
              </SocialButtton>
            ) : null}
            {config?.facebookLogin?.isAvailable ? (
              <SocialButtton
                color="inherit"
                sx={{ order: config.facebookLogin.displayOrder }}
                disabled={isSubmitting}
                tabIndex={getCorrectTabIndex(config.facebookLogin.displayOrder)}
                onClick={() => loginWithSocialNetwork('Facebook')}
              >
                <IconWrapper>
                  <Facebook sx={{ color: '#1877F2', fontSize: '37px' }} />
                </IconWrapper>
                <IconText variant="body1">
                  <Localization
                    id={TranslationKey.Prestep_Facebook}
                    defaultMessage={defaultTranslations.Prestep_Facebook}
                  />
                </IconText>
              </SocialButtton>
            ) : null}
          </SocialWrapper>
        </>
      ) : null}
    </FormProvider>
  );
};
